import type { UsageProviderKind } from "@cmmd-forge/contracts";
import { PanelsTopLeftIcon } from "lucide-react";

import { ClaudeAI, type Icon, OpenAI } from "../Icons";

type UsageProviderPresentation = {
  readonly label: string;
  readonly color: string;
  readonly mark: Icon;
};

/**
 * Exhaustive presentation for every lane the usage contract can report.
 * Declaration order is reused by every chart, table, legend, and skeleton, so
 * adding a lane only requires its contract support and one entry here.
 */
export const PROVIDER_PRESENTATION = {
  codex: {
    label: "Codex",
    color: "var(--foreground)",
    mark: OpenAI,
  },
  claude: {
    label: "Claude Code",
    color: "#d97757",
    mark: ClaudeAI,
  },
  cmmd: {
    label: "CMMD",
    color: "#5b8def",
    mark: PanelsTopLeftIcon as Icon,
  },
} satisfies Record<UsageProviderKind, UsageProviderPresentation>;

/** The chart layers every series from zero, so order only controls how it is read. */
export const PROVIDER_ORDER = Object.keys(PROVIDER_PRESENTATION) as UsageProviderKind[];

/**
 * Lanes with real activity, in reading order and independent of the metric on
 * display.
 *
 * Sorting the summary by whichever metric the toggle shows reorders the rows
 * under the reader when they flip between cost and tokens, and lets a lane
 * appear or vanish for the same window. Filtering a fixed order instead keeps
 * the summary, the chart series, and the period table naming the same lanes in
 * the same places.
 */
export function providersWithUsage(
  totals: readonly {
    readonly provider: UsageProviderKind;
    readonly costUsd: number;
    readonly totalTokens: number;
  }[],
): readonly UsageProviderKind[] {
  const active = new Set(
    totals
      .filter((entry) => entry.totalTokens > 0 || entry.costUsd > 0)
      .map((entry) => entry.provider),
  );
  return PROVIDER_ORDER.filter((provider) => active.has(provider));
}
